export const SECTION_DISPLAY_NAMES = {
  generalAccess: 'General Access',
  crewScheduling: 'Crew Scheduling',
  timeCapture: 'Time Capture',
  zoomEntry: 'Zoom Entry',
  supportAndInsight: 'AI Assistants',
  rolesAndPermissions: 'Roles and Users',
};

// Section keys persisted under permissions.cap, in display order.
export const SECTION_KEYS = Object.keys(SECTION_DISPLAY_NAMES);

// Feature names under the "AI Assistants" section. Shared with the runtime
// access check so the stored value and the check never drift apart.
// NOTE: the stored cap key stays `supportAndInsight` — renaming it would orphan
// the permissions already saved against existing role names.
export const SUPPORT_AND_INSIGHT_SECTION = 'supportAndInsight';
export const VINNY_CHAT_FEATURE = 'Vinny Chat';
export const VYN_INSIGHTS_FEATURE = 'VYN Insights';

// Licence (productLicenseKey) feature values, as toggled by the Super User in
// Licence Management > Manage Features.
export const VINNY_CHAT_LICENCE = 'vinnychat';
export const VYN_INSIGHTS_LICENCE = 'vyninsights';

// Maps a permission section to the licence values that unlock it. A section is
// offered to the Super Admin only when the organisation's licence carries at
// least one of the listed values.
// Sections absent from this map are not sold as licence features and stay
// visible for every organisation: `generalAccess` (stand-in, hyperfind,
// download...) and `rolesAndPermissions` (backed by the always-on
// `usermanagement` feature) apply to the product as a whole.
export const SECTION_LICENCE_FEATURES = {
  crewScheduling: ['crewmanagement', 'scheduleworkmen'],
  timeCapture: ['timetracker'],
  zoomEntry: ['zoomTimeEntry'],
  supportAndInsight: [VYN_INSIGHTS_LICENCE, VINNY_CHAT_LICENCE],
};

// Individual features that carry their own licence value inside a section, so a
// partially licensed section shows only what the organisation actually bought.
export const FEATURE_LICENCE_FEATURES = {
  [VINNY_CHAT_FEATURE]: VINNY_CHAT_LICENCE,
  [VYN_INSIGHTS_FEATURE]: VYN_INSIGHTS_LICENCE,
};

export const FEATURE_TOOLTIPS = {
  'Stand-in Access': 'Act as another user',
  'Hyperfind Access': 'Advanced search access basis UKG',
  'View Audit Log': 'View audit trail',
  'Backdated Entry': 'Edit past schedules/punches',
  Download: 'Download access',
  'Crew & Schedule Management': 'Access to crew and scheduling operations',
  'Crew Template Management': 'Create/reuse templates',
  'Add-ons': 'Add-ons for crew management',
  'Layoff Management': 'Single/mass layoff',
  'Schedule Override': 'Conflicting schedule override',
  'Non-mandatory Event and Task': 'Can schedule without Event&Tasks also',
  'Scan All': 'Can punch-in or punch out unscheduled employees also',
  'Vinny Chat': 'Access to the Vinny chat assistant',
  'VYN Insights': 'Access to the VYN Insights module',
};

export const INITIAL_SECTIONS_DATA = {
  generalAccess: [
    { feature: 'Stand-in Access', subFeature: '-', access: false },
    { feature: 'Hyperfind Access', subFeature: '-', access: false },
    { feature: 'View Audit Log', subFeature: '-', access: false },
    { feature: 'Backdated Entry', subFeature: '-', access: false },
    { feature: 'Download', subFeature: '-', access: false },
  ],
  crewScheduling: [
    {
      feature: 'Crew & Schedule Management',
      subFeature: null,
      access: false,
      isExpandable: true,
      expandedData: [
        { subFeature: 'Crew Management', create: 'Myself', view: 'All', edit: 'All', delete: 'All' },
        { subFeature: 'Schedule Management', create: 'All', view: 'All', edit: 'All', delete: 'None' },
      ],
    },
    {
      feature: 'Crew Template Management',
      subFeature: 'Manage Crew Template',
      access: false,
      create: 'Myself',
      view: 'Myself',
      edit: 'Myself',
      delete: 'Myself',
    },
    {
      feature: 'Add-ons',
      subFeature: null,
      access: null,
      isExpandable: true,
      expandedData: [
        { subFeature: 'Copy Crew', access: false },
        { subFeature: 'Transfer Crew', access: false },
        { subFeature: 'Filters (Crew, Employee, General)', access: false },
      ],
    },
    { feature: 'Layoff Management', subFeature: 'Single/mass layoff', access: false },
    { feature: 'Schedule Override', subFeature: 'Conflicting schedule override', access: false },
    { feature: 'Non-mandatory Event and Task', subFeature: 'Can schedule without event and task also', access: false },
    {
      feature: 'Show Specific Fields',
      subFeature: null,
      access: null,
      isExpandable: true,
      expandedData: [
        { subFeature: 'Dispatch', access: false },
        { subFeature: 'On-Prem', access: false },
        { subFeature: 'Union Ratio', access: false },
      ],
    },
    { feature: 'Employee Schedule Access', subFeature: null, access: false },
    { feature: 'Employee Locator', subFeature: null, access: false },
  ],
  timeCapture: [
    {
      feature: 'Standard Punch',
      subFeature: null,
      access: null,
      isExpandable: true,
      expandedData: [
        { subFeature: 'Time Entry', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
        { subFeature: 'Out Punch', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
      ],
    },
    {
      feature: 'Special Punch',
      subFeature: null,
      access: null,
      isExpandable: true,
      expandedData: [
        { subFeature: 'Mass Check-out', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
        { subFeature: 'Exception Management', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
        { subFeature: 'Speed Check-In', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
      ],
    },
    {
      feature: 'Employee Management',
      subFeature: null,
      access: null,
      isExpandable: true,
      expandedData: [
        { subFeature: 'My Scheduled Crews', create: 'None', view: 'Myself', edit: 'None', delete: 'None', access: false },
        { subFeature: 'Re-assign My Crews', create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
        { subFeature: 'View My Crews', create: 'None', view: 'Myself', edit: 'None', delete: 'None', access: false },
      ],
    },
    { feature: 'Paycode', subFeature: null, create: 'All', view: 'All', edit: 'None', delete: 'None', access: false },
    { feature: 'Scan All', subFeature: null, access: false },
    { feature: 'Generate/Print Receipts & Print History', subFeature: null, access: false },
    { feature: 'Non-mandatory Event and Task', subFeature: null, access: false },
    { feature: 'Default LL Settings', subFeature: null, access: false },
  ],
  zoomEntry: [
    { feature: 'Time Entry', subFeature: null, access: false },
    { feature: 'PayCode Entry', subFeature: null, access: false },
  ],
  supportAndInsight: [
    { feature: VINNY_CHAT_FEATURE, subFeature: '-', access: false },
    { feature: VYN_INSIGHTS_FEATURE, subFeature: '-', access: false },
  ],
  rolesAndPermissions: [
    { feature: 'Manage Roles', subFeature: null, access: false },
    { feature: 'Manage Users', subFeature: null, access: false },
  ],
};

export const SPECIAL_SUBS = [
  'Time Entry',
  'Out Punch',
  'Mass Check-out',
  'Exception Management',
  'Speed Check-In',
  'My Scheduled Crews',
  'Re-assign My Crews',
  'View My Crews',
];
